// Wave 20.49cs — /share-card-embed: bare page that renders a session's
// share card (map + wind still + peak pins + stats) and hands the
// finished image back to the host as a data URL.
//
// Same pattern as /embed/wind and /wake-embed: outside the chrome
// layout, parameter-driven, loaded in a WebView by the mobile app so
// mobile ships the EXACT same pixels as web — one renderer, true
// parity. Params:
//   ?session=<id>   activity id
//   &jwt=<token>    bearer for /activities/:id, /intervals, /weather
//   &units=metric|imperial
//   &format=jpeg|png   (jpeg default — much smaller over the bridge)
// Posts { type:'share-card:image', dataUrl, caption } or
//       { type:'share-card:error', message } via ReactNativeWebView /
// window.parent.

import React, { useEffect, useRef, useState } from 'react';
import { colors } from './theme';
import {
  CARD_H,
  CARD_W,
  buildShareCardModel,
  drawShareCard,
  type PeakLike,
  type ShareableActivity,
} from './services/shareCard';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

function post(payload: Record<string, unknown>) {
  const json = JSON.stringify(payload);
  try {
    const rnwv = (window as any).ReactNativeWebView;
    if (rnwv && typeof rnwv.postMessage === 'function') rnwv.postMessage(json);
  } catch { /* no host */ }
  try {
    if (window.parent && window.parent !== window) window.parent.postMessage(payload, '*');
  } catch { /* cross-origin — ignore */ }
}

export default function ShareCardEmbed() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [status, setStatus] = useState<'working' | 'done' | 'error'>('working');

  useEffect(() => {
    document.body.style.background = colors.bg;
    document.body.style.margin = '0';
    const q = new URLSearchParams(window.location.search);
    const id = q.get('session');
    const jwt = q.get('jwt');
    const units = q.get('units') === 'imperial' ? 'imperial' : 'metric';
    const format = q.get('format') === 'png' ? 'image/png' : 'image/jpeg';
    let cancelled = false;
    (async () => {
      try {
        if (!id || !jwt) throw new Error('missing session or token');
        const headers = { Authorization: `Bearer ${jwt}` };
        const [aRes, iRes] = await Promise.all([
          fetch(`${API}/activities/${encodeURIComponent(id)}`, { headers }),
          fetch(`${API}/activities/${encodeURIComponent(id)}/intervals`, { headers }),
        ]);
        if (!aRes.ok) throw new Error(`activity ${aRes.status}`);
        const activity = (await aRes.json()) as ShareableActivity;
        const intervals: PeakLike[] = iRes.ok ? ((await iRes.json()) as PeakLike[]) : [];
        const model = buildShareCardModel(activity, units, Array.isArray(intervals) ? intervals : []);
        const c = canvasRef.current;
        if (!c || cancelled) return;
        await drawShareCard(c, model, { jwt });
        if (cancelled) return;
        const dataUrl = c.toDataURL(format, format === 'image/jpeg' ? 0.92 : undefined);
        post({ type: 'share-card:image', dataUrl, caption: model.caption, format });
        setStatus('done');
      } catch (e: any) {
        if (cancelled) return;
        post({ type: 'share-card:error', message: 'render failed' });
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <canvas
        ref={canvasRef}
        width={CARD_W}
        height={CARD_H}
        style={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block' }}
        aria-label={status === 'error' ? 'Card unavailable' : 'Share card'}
      />
    </div>
  );
}
